import { Controller } from "@hotwired/stimulus"

// The triage review sheet (triage_runs/_review). The headline, each change's
// line and the confirm button say what confirming does with the boxes ticked
// now, in Paperwork's words. The server renders the first wording
// (Triage::ReviewSheet); this keeps it true as boxes are ticked. Keep the two
// in step. Labels are task button names, so they are set as text, never HTML.
//
// A group decision also has "What to do" (triage_runs/_review_choice): one
// choice control and the field the chosen kind needs. This shows that field,
// words the headline and the button for the kind, and leaves out (box off,
// row hidden) each task that cannot take it, as the server said per task
// (data-allow), or that the agent found does not fit the person's checked
// answer (data-answer). The server checks all of it again on Confirm; this
// only keeps the screen honest. Confirm stays off until every ticked change
// is ready: an answer checked for exactly the words in its field, a person
// or a contact chosen, a reason or note written.
//
// With each task's own button, the workflow changes the agent planned
// (Triage::MemberPlan) apply while their box is ticked: the line and the
// button then say so ("…, then cancel 7 workflows"), each row shows its
// change, and a task whose workflow cannot take it (data-workflow-out) is
// left out.
const NOTHING = "No tasks selected"
const VERBS = { complete: "Complete", cancel: "Cancel" }
const CHECKED = ["own_answer", "reject"]

const tasks = (count) => (count === 1 ? "1 task" : `${count} tasks`)
const oneLine = (label) => label.replace(/ /g, " ")
const sentence = (parts) => (parts.length < 3 ? parts.join(" and ") : `${parts.slice(0, -1).join(", ")}, and ${parts.at(-1)}`)

function buttonsPhrase(boxes) {
  const count = boxes.length
  const labels = [...new Set(boxes.map((box) => box.dataset.effect || ""))]
  if (labels.length === 1 && labels[0]) return `${labels[0]} on ${tasks(count)}`

  const verbs = [...new Set(boxes.map((box) => box.dataset.verb || ""))]
  const verb = verbs.length === 1
    ? VERBS[verbs[0]] || "Change"
    : verbs.every((name) => name in VERBS) ? "Close" : "Change"
  return `${verb} ${tasks(count)}`
}

// "cancel 7 workflows and keep 2 workflows on hold" for the boxes' planned
// workflow changes (data-workflow); "" when none has one.
export function workflowPhrase(boxes) {
  const found = tally(boxes, "workflow")
  return sentence([...found].map(([verb, count]) => {
    const workflows = count === 1 ? "1 workflow" : `${count} workflows`
    if (verb === "cancel") return `cancel ${workflows}`
    if (verb === "hold") return `put ${workflows} on hold`
    return `keep ${workflows} on hold`
  }))
}

// What confirming does to these ticked boxes. choice: the group decision's
// chosen kind, person and contact, when the change has one.
export function phrase(change, boxes, choice = null) {
  const count = boxes.length
  if (count === 0) return NOTHING
  const { kind, listName } = change.dataset
  if (choice) {
    switch (choice.kind) {
      case "own_answer": return `Complete ${tasks(count)} with your answer`
      case "reject": return `Reject ${tasks(count)}`
      case "hold": return `Hold ${tasks(count)}`
      case "defer": return choice.person ? `Defer ${tasks(count)} to ${choice.person}` : `Defer ${tasks(count)}`
      case "note": return `Add this note to ${tasks(count)}`
      case "contact": return choice.contact ? `Map ${tasks(count)} to ${choice.contact}` : `Map ${tasks(count)} to a contact`
    }
  }
  if (kind === "note") return `Add this note to ${tasks(count)}`
  if (kind === "list") return `Move ${count === 1 ? "1 workflow" : `${count} workflows`} to “${listName}”`
  const workflows = choice?.workflows ? workflowPhrase(boxes) : ""
  return workflows ? `${buttonsPhrase(boxes)}, then ${workflows}` : buttonsPhrase(boxes)
}

function split(boxes) {
  const counts = tally(boxes, "effect")
  if (counts.size < 2) return ""
  // A button name keeps to one line (non-breaking spaces), so it reads as one name.
  return `Each task uses its own button: ${sentence([...counts].map(([label, count]) => `“${oneLine(label)}” on ${count}`))}.`
}

function tally(boxes, key) {
  const counts = new Map()
  boxes.forEach((box) => {
    const label = box.dataset[key]
    if (label) counts.set(label, (counts.get(label) || 0) + 1)
  })
  return counts
}

// “A” on 10 and “B” on 12. / “A” on each.
export function counts(boxes, key) {
  const found = tally(boxes, key)
  if (found.size === 0) return ""
  if (found.size === 1) return `“${oneLine([...found.keys()][0])}” on each.`
  return `${sentence([...found].map(([label, count]) => `“${oneLine(label)}” on ${count}`))}.`
}

export default class extends Controller {
  static targets = ["headline", "submit", "submitLabel", "change"]

  connect() {
    this.refresh()
  }

  toggleGroup(event) {
    const change = event.target.closest("[data-triage-review-target~='change']")
    this.boxes(change).filter((box) => !box.disabled).forEach((box) => { box.checked = event.target.checked })
    this.refresh()
  }

  // Return in a one-line field never submits the confirm.
  keep(event) {
    if (event.key === "Enter") event.preventDefault()
  }

  refresh() {
    if (!this.hasChangeTarget) return

    let ready = true
    const phrases = this.changeTargets.map((change) => {
      const choice = this.choose(change)
      const boxes = this.boxes(change)
      const usable = boxes.filter((box) => !box.disabled)
      const ticked = usable.filter((box) => box.checked)
      const line = change.querySelector("[data-role='effect']")
      if (line) line.textContent = ticked.length ? phrase(change, ticked, choice) : phrase(change, usable, choice)
      const splitLine = change.querySelector("[data-role='split']")
      if (splitLine) {
        splitLine.textContent = choice ? "" : split(ticked)
        splitLine.hidden = !splitLine.textContent
      }
      const group = change.querySelector("[data-role='group']")
      // Mixed reads as unchecked underneath, so a click on it ticks them all.
      if (group) {
        group.checked = usable.length > 0 && ticked.length === usable.length
        group.indeterminate = ticked.length > 0 && ticked.length < usable.length
      }
      change.classList.toggle("is-off", ticked.length === 0)
      if (choice) this.word(change, choice, ticked)
      if (ticked.length && choice && !choice.ready) ready = false
      return { count: ticked.length, text: phrase(change, ticked, choice) }
    })

    const total = phrases.reduce((sum, { count }) => sum + count, 0)
    const text = total === 0
      ? NOTHING
      : phrases.length === 1 ? phrases[0].text : `Apply ${total} ${total === 1 ? "change" : "changes"}`
    this.headlineTarget.textContent = text
    // While Turbo submits, the button reads "Applying…" and has no label.
    if (this.hasSubmitLabelTarget) {
      this.submitLabelTarget.textContent = text
      this.submitLabelTarget.title = text
    }
    this.submitTarget.disabled = total === 0 || !ready
  }

  // A group decision's "What to do": which kind is chosen, its field shown,
  // and each box on or off for it. Returns { kind, person, contact, ready },
  // or null for a change without a choice.
  choose(change) {
    const root = change.querySelector("[data-role='choice']")
    if (!root) return null

    const segment = root.querySelector("[data-role='segment']:checked")?.value || "complete"
    const completion = root.querySelector("[data-role='complete-with']:checked")?.value || "buttons"
    const kind = segment === "complete" ? completion : segment
    change.dataset.choice = kind
    // Own answer's field sits inside Complete's panel.
    root.querySelectorAll("[data-panel]").forEach((panel) => {
      panel.hidden = panel.dataset.panel === "own_answer" ? kind !== "own_answer" : panel.dataset.panel !== segment
    })
    // The people load the first time Defer is chosen.
    const people = root.querySelector("[data-role='people']")
    if (kind === "defer" && people && !people.getAttribute("src") && people.dataset.src) people.setAttribute("src", people.dataset.src)
    // So do the contacts, the first time Map to… is chosen.
    const contacts = root.querySelector("[data-role='contacts']")
    if (kind === "contact" && contacts && !contacts.getAttribute("src") && contacts.dataset.src) contacts.setAttribute("src", contacts.dataset.src)

    const text = root.querySelector(`[data-role='text'][data-kind='${kind}']`)?.value.trim() || ""
    const { checkedKind, checkedText, answerState } = root.dataset
    // The check on file is for this kind and exactly these words.
    const current = CHECKED.includes(kind) && checkedKind === kind && (checkedText || "") === text && Boolean(text)
    const done = current && answerState === "done"

    const workflowBox = root.querySelector("[data-role='workflows']")
    const workflows = kind === "buttons" && Boolean(workflowBox?.checked)
    change.dataset.workflows = workflows ? "on" : "off"
    const workflowOption = root.querySelector("[data-role='workflows-option']")
    if (workflowOption) workflowOption.hidden = kind === "own_answer"

    this.boxes(change).forEach((box) => {
      const allowed = (box.dataset.allow || "").split(" ").includes(kind) && (!done || box.dataset.answer === "fits") &&
        !(workflows && box.dataset.workflowOut)
      box.disabled = !allowed
      box.closest(".triage-item")?.classList.toggle("is-out", !allowed)
    })
    change.querySelectorAll("[data-out-for]").forEach((block) => { block.hidden = block.dataset.outFor !== kind })
    change.querySelectorAll("[data-answer-out]").forEach((block) => { block.hidden = !(done && block.dataset.answerOut === kind) })
    // The blocks only: each task's own box also carries data-workflow-out.
    change.querySelectorAll("details[data-workflow-out]").forEach((block) => { block.hidden = !workflows })
    change.querySelectorAll("[data-controller~='triage-list']").forEach((list) => list.dispatchEvent(new CustomEvent("triage:rows")))

    if (CHECKED.includes(kind)) {
      const waiting = current && answerState === "checking"
      const failed = current && answerState === "failed"
      const status = root.querySelector(`[data-role='answer-status'][data-kind='${kind}']`)
      if (status) {
        status.textContent = done ? root.dataset.statusDone
          : waiting ? root.dataset.statusChecking
            : failed ? root.dataset.statusFailed : root.dataset.statusUnchecked
      }
      const check = root.querySelector(`[data-role='check'][data-kind='${kind}']`)
      if (check) {
        check.hidden = done || waiting
        check.disabled = !text
      }
    }

    const select = root.querySelector("[data-role='person']")
    const person = kind === "defer" && select?.value ? select.selectedOptions[0]?.textContent.trim() : ""
    const contact = kind === "contact" ? this.contactName(root) : ""
    let ready
    if (kind === "buttons") ready = true
    else if (kind === "defer") ready = Boolean(person)
    else if (kind === "contact") ready = Boolean(contact)
    else if (CHECKED.includes(kind)) ready = done
    else ready = Boolean(text)
    return { kind, person, contact, ready, workflows }
  }

  // The chosen contact's name, from the picker's own options by the chosen
  // id (the search field holds the query while the person types). Before
  // the picker loads, the saved choice's name.
  contactName(root) {
    const id = root.querySelector("[data-role='contact']")?.value
    if (!id) return ""
    const picker = root.querySelector("[data-role='contact-picker']")
    if (!picker) return root.querySelector(".triage-choice__contact")?.value.trim() || ""
    if (!this.contactOptions?.has(picker)) {
      this.contactOptions ||= new WeakMap()
      let options = []
      try { options = JSON.parse(picker.dataset.searchableSelectOptionsValue || "[]") } catch { options = [] }
      this.contactOptions.set(picker, new Map(options.map((option) => [String(option.value), option.label])))
    }
    return this.contactOptions.get(picker).get(String(id)) || ""
  }

  // The lines under the picker that count the ticked tasks.
  word(change, choice, ticked) {
    const root = change.querySelector("[data-role='choice']")
    const buttons = root.querySelector("[data-role='button-counts']")
    if (buttons && choice.kind === "buttons") buttons.textContent = counts(ticked, "effect")
    const workflowLine = root.querySelector("[data-role='workflow-line']")
    if (workflowLine) workflowLine.textContent = workflowPhrase((ticked.length ? ticked : this.boxes(change).filter((box) => !box.disabled)).filter((box) => !box.dataset.workflowOut))
    const rejects = root.querySelector("[data-role='reject-counts']")
    if (rejects && choice.kind === "reject") rejects.textContent = counts(ticked, "effectReject")
    const defer = root.querySelector("[data-role='defer-line']")
    if (defer) defer.textContent = choice.kind === "defer" && choice.person && ticked.length ? `${choice.person} gets one email that lists the ${tasks(ticked.length)}.` : ""
  }

  boxes(change) {
    return [...change.querySelectorAll("input[name='selected_item_ids[]'][type='checkbox']")]
  }
}
